import parse, {
  domToReact,
  Element,
  type DOMNode,
  type HTMLReactParserOptions,
} from 'html-react-parser';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

import { DeepResearchCard, DeepResearchCompContent, DeepResearchCompTitle } from '../shared';

import type {
  AnswerClaim,
  TopicAnalysisAnswer,
  TopicAnalysisComponentTheme,
} from '@/entities/ask-syncly/types';
import { ReferencedPostsModal } from '@/features/(insight)/referenced-posts/ui/referenced-posts-modal';
import { CLAIM_MAX_POST_COUNT } from '@/features/(insight)/referenced-posts/ui/referenced-posts-page';
import { Badge } from '@/shared/components/shadcn/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/shadcn/collapsible';
import { cn } from '@/shared/utils/cn';
import { mdConverter } from '@/shared/utils/md-converter';
import { captureException } from '@sentry/react';
import { ThemePostContainer } from './theme-post-container';

export function Topic({
  session_id,
  answer,
  block_id,
}: {
  session_id: string;
  answer: TopicAnalysisAnswer;
  block_id: string;
}) {
  const { title, content, overview, themes } = answer;
  const [refModalOpen, setRefModalOpen] = useState<null | string>(null);
  const onClaimClick = (id: string) => {
    setRefModalOpen(id);
  };
  return (
    <DeepResearchCard>
      <DeepResearchCompTitle>{title}</DeepResearchCompTitle>
      <DeepResearchCompContent className="mt-5">{content}</DeepResearchCompContent>
      <h3 className="mt-16 text-xl font-bold">Overview</h3>
      <div className="mt-5 text-base text-gray-700">
        <ClaimContent
          content={overview.content}
          claims={overview.claims}
          onClaimClick={onClaimClick}
        />
      </div>
      {themes && themes.length > 0 && (
        <>
          <DeepResearchCompTitle className="mt-16">Themes</DeepResearchCompTitle>
          <div className="mt-6 flex flex-col gap-2">
            {themes.map((theme) => (
              <TopicTheme
                key={theme.id}
                session_id={session_id}
                answer_id={answer.id}
                theme={theme}
                block_id={block_id}
              />
            ))}
          </div>
        </>
      )}
      {block_id && refModalOpen && (
        <ReferencedPostsModal
          isOpen={refModalOpen !== null}
          onClose={() => setRefModalOpen(null)}
          session_id={session_id}
          block_id={block_id}
          answer_id={answer.id}
          parts="overview"
          part_id={refModalOpen}
        />
      )}
    </DeepResearchCard>
  );
}

export function TopicTheme({
  session_id,
  answer_id,
  block_id,
  theme,
}: {
  session_id: string;
  answer_id: string;
  block_id: string;
  theme: TopicAnalysisComponentTheme;
}) {
  const { name, sentiment, summary } = theme;
  const [isOpen, setIsOpen] = useState(false);
  const sentimentText = sentiment
    ? sentiment.charAt(0).toUpperCase() + sentiment.slice(1)
    : 'Neutral';
  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="w-full rounded-lg border border-gray-200"
    >
      <CollapsibleTrigger className="w-full">
        <div className="flex w-full items-center justify-between gap-2 px-4 py-3">
          <div className="flex flex-col items-start gap-2">
            <span className="text-left text-base font-semibold text-gray-800">{name}</span>
            <Badge
              className={cn(
                'bg-muted-foreground rounded-full px-2.5 py-0.5 text-xs font-semibold text-white',
                sentiment === 'positive' && 'bg-[#2a9d90]',
                sentiment === 'negative' && 'bg-[#dc2626]',
              )}
            >
              {sentimentText}
            </Badge>
          </div>
          <ChevronDown
            className={cn('size-5 transition-transform duration-200', isOpen && '-rotate-180')}
          />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="h-px w-full bg-gray-200" />
        <div className="flex flex-col px-4 py-3">
          <h3 className="text-base font-medium">Summary</h3>
          <DeepResearchCompContent className="mt-2">{summary}</DeepResearchCompContent>
          <h3 className="mt-4 flex h-7 items-center text-base font-medium">Example Posts</h3>
          <ThemePostContainer
            params={{ session_id, answer_id, theme_id: theme.id, block_id }}
            className="mt-2"
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

const ClaimContent = (props: {
  content: string;
  claims: AnswerClaim[];
  onClaimClick: (id: string) => void;
}) => {
  const { content, onClaimClick } = props;

  const htmlString = mdConverter.makeHtml(content);

  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (domNode instanceof Element && 'attribs' in domNode && domNode.name === 'claim') {
        const id = domNode?.attribs?.['data-claim-id'];
        const count = Number(domNode?.attribs?.['data-num-grounding-posts']);

        if (!id || typeof count !== 'number') {
          captureException(new Error('Invalid claim'));
          return null;
        }

        return (
          <ClaimItem
            id={id}
            selected={false}
            count={count}
            onClick={(e) => {
              e.preventDefault();
              onClaimClick(id);
            }}
          />
        );
      }
      if (domNode instanceof Element && 'attribs' in domNode && domNode.name === 'p') {
        return <div>{domToReact(domNode.children as DOMNode[], options)}</div>;
      }
    },
  };

  const parsed = parse(htmlString, options);

  return parsed;
};

type ClaimItemProps = {
  id: string;
  selected: boolean;
  onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  count: number;
};

export const ClaimItem = (props: ClaimItemProps) => {
  const { onClick, count, selected } = props;
  return (
    <span
      className={cn(
        'mx-1 inline-block -translate-y-0.5 cursor-pointer rounded-full border bg-gray-200 px-1.5 text-xs font-semibold transition-colors duration-200 hover:bg-gray-300',
        selected && 'bg-gray-800 text-white hover:bg-gray-800',
      )}
      onClick={onClick}
    >
      {count >= CLAIM_MAX_POST_COUNT ? `${CLAIM_MAX_POST_COUNT}+` : count}
    </span>
  );
};
